import { Injectable, Inject } from "@angular/core";
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService, MsalBroadcastService } from "@azure/msal-angular";
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, InteractionType, PopupRequest, RedirectRequest, SilentRequest } from "@azure/msal-browser";
import { filter, Subject } from "rxjs";
import { loginRequest } from "./auth.config";
import { CachingService } from "../shared/services/caching.service";
import { PlatformDetectionService } from "./oauth2/platform-detection.service";
import { MobileOAuth2Service } from "./oauth2/mobile-oauth2.service";
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  profile: any;
  loginDisplay = false;
  isIframe = false;
  private accountGuid: string;

  private isAuthenticationInitialized = false;
  // Subject to emit login success events
  private loginSuccessSubject = new Subject<AuthenticationResult>();

  // Expose the subject as an observable for components to subscribe
 public loginSuccess$ = this.loginSuccessSubject.asObservable();
  private enableDebugLogs = false; // Set to true to enable debug logging
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private cachingService: CachingService,
    private platformDetection: PlatformDetectionService,
    private mobileOAuth2: MobileOAuth2Service
  ) {}

  public async init() {
    if(this.enableDebugLogs) {
      console.log('=== AuthService.init() ===');
      console.log('Platform:', this.platformDetection.getPlatform());
      console.log('isNative:', this.platformDetection.isNative);
    }
    // Skip MSAL initialization on native platforms
    if (this.platformDetection.isNative) {
      if(this.enableDebugLogs)
      console.log('Native platform detected - skipping MSAL initialization');
      this.isAuthenticationInitialized = true;

      // Check if user is already authenticated via OAuth2
      // If not, trigger mobile OAuth2 login
      const isAuthenticated = await this.mobileOAuth2.isAuthenticated();
      if(this.enableDebugLogs)
      console.log('Mobile OAuth2 authenticated:', isAuthenticated);

      if (!isAuthenticated) {
        if(this.enableDebugLogs)
        console.log('User not authenticated - triggering mobile OAuth2 login');
        await this.mobileOAuth2.login();
      } else {
        if(this.enableDebugLogs)
        console.log('User already authenticated via mobile OAuth2');
        // Trigger login success to initialize the app
        this.loginSuccessSubject.next({} as any);
      }

      return;
    }

    // Web platform - initialize MSAL
    if(this.enableDebugLogs)
    console.log('Web platform detected - initializing MSAL');
    await this.authService.instance.initialize(); // Ensure MSAL is initialized
    this.setLoginDisplay();
    this.authService.instance.enableAccountStorageEvents(); // Optional

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
         // Trigger the login success subject

         this.excractAccountGUIDFromUrl();
        this.loginSuccessSubject.next(payload);
      });

      this.isAuthenticationInitialized = true;
  }

  private excractAccountGUIDFromUrl() {
    this.accountGuid = this.getAccountGUIDFromUrl();
  }

  public getAccountGUIDFromUrl() {
    const url = window.location.href;
    const regex = new RegExp("[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?");
    const guidMatch = regex.exec(url);
    return guidMatch && guidMatch[0]?.toString() ? guidMatch[0]?.toString() : '';
  }

  public isAppInitialized(): boolean {
    return this.isAuthenticationInitialized;
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }


  checkAndSetActiveAccount() {
    const accounts = this.authService.instance.getAllAccounts(); // Fetch all accounts once

    if (accounts.length === 0) {
      if(this.enableDebugLogs)
      console.log('No accounts found. User is not authenticated.');
      return;
    }

    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount) {
      // Set the first account as the active account if none is set
      activeAccount = accounts[0];
      this.authService.instance.setActiveAccount(activeAccount);
      if(this.enableDebugLogs)
      console.log('No active account found. Setting the first account as active:', activeAccount);
    }

    // Emit login success for the active account
    if(this.enableDebugLogs)
    console.log('Active account is authenticated. Emitting login success:', activeAccount);
    this.excractAccountGUIDFromUrl();
    this.loginSuccessSubject.next({ account: activeAccount } as AuthenticationResult);
  }

  getAccountGuid(): string {
    let guid = structuredClone(this.accountGuid);
    this.accountGuid = '';
    return guid;
  }

  login() {
    // Check if running on native platform (iOS/Android)
    if(this.enableDebugLogs){
    console.log('=== AuthService.login() Debug ===');
    console.log('Platform:', this.platformDetection.getPlatform());
    console.log('isNative:', this.platformDetection.isNative);
    console.log('isWeb:', this.platformDetection.isWeb);
    console.log('isAndroid:', this.platformDetection.isAndroid);
    console.log('isIOS:', this.platformDetection.isIOS);
    console.log('================================');
    }
    // Show alert for debugging
    const debugMsg = `Platform: ${this.platformDetection.getPlatform()}\nisNative: ${this.platformDetection.isNative}\nUsing: ${this.platformDetection.isNative ? 'MobileOAuth2' : 'MSAL'}`;
    alert(debugMsg);

    if (this.platformDetection.isNative) {
      if(this.enableDebugLogs)
      console.log('Using MobileOAuth2Service for native platform');
      return this.mobileOAuth2.login();
    }

    // Web platform - use MSAL
    if(this.enableDebugLogs)
    console.log('Using MSAL for web platform');
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService
          .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
            this.excractAccountGUIDFromUrl();
            this.loginSuccessSubject.next(response); // Trigger login success event
          });
      } else {
        this.authService.loginPopup().subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
          this.excractAccountGUIDFromUrl();
          this.loginSuccessSubject.next(response); // Trigger login success event
        });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
        } as RedirectRequest);
      } else {
        this.authService.loginRedirect();
      }
    }
  }

  logout() {
    // Check if running on native platform (iOS/Android)
    if (this.platformDetection.isNative) {
      return this.mobileOAuth2.logout();
    }

    // Web platform - use MSAL
    // Clear the stored account ID from localStorage
    localStorage.removeItem('selectedAccountId');
    // Clear the stored account UID from cache (follows same pattern as AID)
    this.cachingService.removeAccountUID();
    // Clear the cached backend API URL
    this.cachingService.removeBackendAPIUrl();

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        account: this.authService.instance.getActiveAccount(),
      });
    } else {
      this.authService.logoutRedirect({
        account: this.authService.instance.getActiveAccount(),
      });
    }
  }

  /**
   * Checks if the user is authenticated by verifying if accounts are available.
   * @returns boolean - True if the user is authenticated, otherwise false.
   */
  isAuthenticated(): boolean {
    // Check if running on native platform (iOS/Android)
    if (this.platformDetection.isNative) {
      return this.mobileOAuth2.isAuthenticated();
    }

    // Web platform - use MSAL
    return this.authService.instance.getAllAccounts().length > 0;
  }

  getActiveEmployeeEmail(): string {
    if(this.enableDebugLogs)
    console.log('=== getActiveEmployeeEmail() called ===');
    if(this.enableDebugLogs)
    console.log('Platform isNative:', this.platformDetection.isNative);

    // Mobile platform - extract from OAuth2 ID token
    if (this.platformDetection.isNative) {
      const idToken = this.mobileOAuth2.getIdToken();
      if(this.enableDebugLogs)
      console.log('Mobile ID token exists:', !!idToken);

      if (idToken) {
        try {
          // Parse JWT to extract email claim
          const payload = this.parseJwt(idToken);
          if(this.enableDebugLogs)
          console.log('Parsed JWT payload:', payload);
          const email = payload.email || payload.preferred_username || payload.upn || undefined;
          if(this.enableDebugLogs)
          console.log('Extracted email:', email);
          return email;
        } catch (error) {
          if(this.enableDebugLogs)
          console.error('Error extracting email from mobile ID token:', error);
          return undefined;
        }
      }
      if(this.enableDebugLogs)
      console.warn('No ID token found for mobile platform');
      return undefined;
    }

    // Web platform - use MSAL
    const activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount) {
      return undefined;
    }
    return activeAccount.username || undefined;
  }

  /**
   * Parses a JWT token and returns the payload
   * @param token - The JWT token to parse
   * @returns The decoded payload object
   */
  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return {};
    }
  }

  /**
   * Retrieves the Bearer token for API authorization.
   * @returns A Promise<string> containing the access token or an error.
   */
  async getBearerToken(): Promise<string> {
    const timestamp = new Date().toISOString();

    // Check if running on native platform (iOS/Android)
    if (this.platformDetection.isNative) {
      if(this.enableDebugLogs)
      console.log(`[${timestamp}] [MOBILE] getBearerToken() - Using mobile OAuth2 service`);
      return await this.mobileOAuth2.getAccessToken();
    }

    // Web platform - use MSAL
    if(this.enableDebugLogs)
    console.log(`[${timestamp}] [WEB] getBearerToken() - Using MSAL`);
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount) {
      if(this.enableDebugLogs)
      console.error(`[${timestamp}] [WEB] No active account found`);
      throw new Error('No active account found. Please login.');
    }
if(this.enableDebugLogs)
    console.log(`[${timestamp}] [WEB] Active account:`, {
      username: activeAccount.username,
      homeAccountId: activeAccount.homeAccountId,
      environment: activeAccount.environment
    });

    let scopes: string[] =  loginRequest.scopes;

    if (typeof this.msalGuardConfig.authRequest === 'object' && this.msalGuardConfig.authRequest?.scopes) {
      scopes = this.msalGuardConfig.authRequest.scopes;
    }

    console.log(`[${timestamp}] [WEB] Requesting token with scopes:`, scopes);

    const tokenRequest: SilentRequest = {
      account: activeAccount,
      scopes,
    };

    try {
      if(this.enableDebugLogs)
      console.log(`[${timestamp}] [WEB] Calling acquireTokenSilent()...`);
      const response: AuthenticationResult = await this.authService.acquireTokenSilent(tokenRequest).toPromise();
      if(this.enableDebugLogs) {
      console.log(`[${timestamp}] [WEB] ✅ Token acquired successfully via silent refresh`);
      console.log(`[${timestamp}] [WEB] Token expiration:`, response.expiresOn?.toISOString());
      }
      return response.accessToken;
    } catch (error) {
      console.error(`[${timestamp}] [WEB] ❌ acquireTokenSilent() failed:`, error);
      console.error(`[${timestamp}] [WEB] Error details:`, {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : JSON.stringify(error),
        stack: error instanceof Error ? error.stack : undefined
      });

      // Optional: Handle fallback to interactive login
      console.warn(`[${timestamp}] [WEB] Attempting interactive login fallback...`);
      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
        if(this.enableDebugLogs)
        console.log(`[${timestamp}] [WEB] Using popup interaction`);
        const popupResponse = await this.authService.loginPopup(tokenRequest as PopupRequest).toPromise();
        if(this.enableDebugLogs)
        console.log(`[${timestamp}] [WEB] ✅ Token acquired via popup login`);
        return popupResponse.accessToken;
      } else {
        if(this.enableDebugLogs)
        console.log(`[${timestamp}] [WEB] Using redirect interaction`);
        this.authService.loginRedirect(tokenRequest as RedirectRequest);
        throw new Error('Redirecting for login. No token available.');
      }
    }
  }


  /**
   * Retrieves the Bearer token for API authorization.
   * @returns A Promise<string> containing the access token or an error.
   */
  async getIdToken(): Promise<string> {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount) {
      throw new Error('No active account found. Please login.');
    }

    let scopes: string[] =  loginRequest.scopes;

    if (typeof this.msalGuardConfig.authRequest === 'object' && this.msalGuardConfig.authRequest?.scopes) {
      scopes = this.msalGuardConfig.authRequest.scopes;
    }

    const tokenRequest: SilentRequest = {
      account: activeAccount,
      scopes,
    };

    try {
      const response: AuthenticationResult = await this.authService.acquireTokenSilent(tokenRequest).toPromise();
      if(this.enableDebugLogs)
      console.log(response);
      return response.idToken;
    } catch (error) {

      // Optional: Handle fallback to interactive login
      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
        const popupResponse = await this.authService.loginPopup(tokenRequest as PopupRequest).toPromise();
        return popupResponse.idToken;
      } else {
        this.authService.loginRedirect(tokenRequest as RedirectRequest);
        throw new Error('Redirecting for login. No token available.');
      }
    }
  }
}
